import { Buffer } from 'buffer';
import { Keypair, Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { buyToken, sellToken } from './jupiterSwap';
import {
  logBotTrade, logBotEvent,
  notifyBotStart, notifyBotStop,
  notifyBuy, notifySell,
  notifyStopLoss, notifyTakeProfit,
  notifyError,
} from './botLogger';
import { supabase } from '../supabase';

const HELIUS    = 'https://api.helius.xyz/v0';
const TOKEN_PGM = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
const SIM_KEY   = 'sim-wallet-balance';
function getHeliusKey() {
  return localStorage.getItem('helius-api-key') || process.env.REACT_APP_HELIUS_API_KEY || '';
}
function getHeliusRpcUrl() {
  const k = getHeliusKey();
  return k ? `https://mainnet.helius-rpc.com/?api-key=${k}` : 'https://api.mainnet-beta.solana.com';
}
function getHeliusWsUrl() {
  const k = getHeliusKey();
  return k ? `wss://mainnet.helius-rpc.com/?api-key=${k}` : 'wss://api.mainnet-beta.solana.com';
}

// Cache prix (TTL 9s) — expire avant le pnlInterval de 10s
const priceCache = new Map();
const PRICE_CACHE_TTL = 9_000;

// Mutex global : mints en cours d'achat pour éviter le double-achat
const buyLocks = new Set();

async function getSolPrice() {
  try {
    const r = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT');
    return parseFloat((await r.json()).price) || 150;
  } catch { return 150; }
}

function getSimBalance() {
  try { return parseFloat(localStorage.getItem(SIM_KEY) || '10'); } catch { return 10; }
}
function setSimBalance(bal) {
  try { localStorage.setItem(SIM_KEY, String(Math.max(0, bal))); } catch {}
}


//  Sauvegarder trade dans Supabase
async function saveTradeToSupabase(tradeData) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    await supabase.from('trades').insert({
      user_id: session.user.id,
      symbol: tradeData.symbol,
      mint: tradeData.mint,
      entree_ts: tradeData.entreeTs,
      achat_usd: tradeData.achatUsd || 0,
      vente_usd: tradeData.venteUsd || 0,
      pnl_usd: tradeData.pnlUsd || 0,
      roi: tradeData.roi || 0,
      resultat: tradeData.resultat || 'En cours',
      is_sim: tradeData.isSim || false,
      wallet_suivi: tradeData.walletSuivi || '',
      tx_hash: tradeData.txHash || '',
      token_amount: tradeData.tokenAmount || 0,
      prix_entree: tradeData.prixEntree || 0,
      bot_wallet_id: tradeData.botWalletId || null,
    });
  } catch(e) { console.warn('Supabase trade save error:', e.message); }
}

async function simBuy(mint, amountSol, solPrice, walletId = 'sim') {
  try {
    const priceData = await getTokenPrice(mint);
    if (!priceData) throw new Error('Token introuvable');
    const achatUsd = amountSol * solPrice;
    const tokenAmount = achatUsd / priceData.priceUsd;
    const currentBal = getSimBalance();
    if (currentBal < amountSol) throw new Error('Solde sim insuffisant');
    setSimBalance(currentBal - amountSol);
    const newTrade = {
      symbol: priceData.symbol, mint,
      entreeTs: Math.floor(Date.now() / 1000),
      achatUsd, venteUsd: 0, pnlUsd: 0, roi: 0,
      resultat: 'En cours', walletSuivi: 'Manuel',
      txHash: 'sim-'+ Date.now(),
      botWalletId: walletId, tokenAmount,
      prixEntree: priceData.priceUsd, isSim: true,
    };
    logBotTrade({ ...newTrade });
    //  Sauvegarder dans Supabase
    await saveTradeToSupabase(newTrade);
    logBotEvent('Sim achat : '+ priceData.symbol, { sol: amountSol, tokens: tokenAmount.toFixed(2) });
    return { symbol: priceData.symbol, tokenAmount, achatUsd, priceUsd: priceData.priceUsd };
  } catch (e) {
    logBotEvent('Sim achat échoué : '+ e.message);
    throw e;
  }
}

async function simSell(mint, pctToSell, solPrice, walletId = 'sim') {
  try {
    const priceData = await getTokenPrice(mint);
    if (!priceData) throw new Error('Token introuvable');

    // Chercher d'abord dans localStorage, puis dans Supabase (trades VPS)
    let trade = null;
    const localTrades = JSON.parse(localStorage.getItem('bot-trades') || '[]');
    trade = localTrades.find(t => t.mint === mint && t.resultat === 'En cours' && t.botWalletId === walletId);

    if (!trade) {
      // Chercher sans filtre walletId (trades VPS sim)
      trade = localTrades.find(t => t.mint === mint && t.resultat === 'En cours');
    }

    if (!trade) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data, error } = await supabase.from('trades')
          .select('*').eq('user_id', session.user.id).eq('mint', mint)
          .eq('resultat', 'En cours').eq('is_sim', true)
          .order('entree_ts', { ascending: false }).limit(1);
        const row = !error && data?.length > 0 ? data[0] : null;
        if (row) {
          trade = { mint: row.mint, symbol: row.symbol, tokenAmount: row.token_amount, achatUsd: row.achat_usd, botWalletId: walletId, resultat: 'En cours', _supabaseId: row.id };
        }
      }
    }

    if (!trade) throw new Error('Position sim introuvable');
    const tokenAmount = (trade.tokenAmount || 0) * (pctToSell / 100);
    const venteUsd = tokenAmount * priceData.priceUsd;
    const solReçu = venteUsd / solPrice;
    setSimBalance(getSimBalance() + solReçu);
    const pnlUsd = venteUsd - (trade.achatUsd * pctToSell / 100);
    const roi = trade.achatUsd > 0 ? (pnlUsd / (trade.achatUsd * pctToSell / 100)) * 100 : 0;

    if (pctToSell >= 100) {
      closeTrade(mint, venteUsd, pnlUsd, roi, walletId);
      const supabaseId = trade._supabaseId || trade.id;
      if (supabaseId) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            await supabase.from('trades').update({
              vente_usd: venteUsd, pnl_usd: pnlUsd, roi,
              resultat: pnlUsd >= 0 ? 'Gagnant' : 'Perdant',
              sortie_ts: Math.floor(Date.now() / 1000),
            }).eq('id', supabaseId);
          }
        } catch(e) { logBotEvent('Sim vente sync Supabase échouée : ' + e.message); }
      }
    } else {
      const factor = 1 - pctToSell / 100;
      const trades = JSON.parse(localStorage.getItem('bot-trades') || '[]');
      const updatedTrades = trades.map(t => {
        const match = t.mint === mint && t.resultat === 'En cours';
        const walletMatch = walletId ? t.botWalletId === walletId : true;
        if (match && walletMatch) {
          return { ...t, tokenAmount: (t.tokenAmount || 0) * factor, achatUsd: (t.achatUsd || 0) * factor };
        }
        return t;
      });
      localStorage.setItem('bot-trades', JSON.stringify(updatedTrades));
      const supabaseId = trade._supabaseId || trade.id;
      if (supabaseId) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            await supabase.from('trades').update({
              token_amount: (trade.tokenAmount || 0) * factor,
              achat_usd: (trade.achatUsd || 0) * factor,
            }).eq('id', supabaseId);
          }
        } catch(e) { logBotEvent('Sim vente partielle sync Supabase échouée : ' + e.message); }
      }
    }

    logBotEvent('Sim vente : '+ priceData.symbol, { pct: pctToSell, pnlUsd: pnlUsd.toFixed(2) });
    return { venteUsd, pnlUsd, roi, solReçu };
  } catch (e) {
    logBotEvent('Sim vente échouée : '+ e.message);
    throw e;
  }
}

async function decryptWallet(walletEntry, password) {
  const { salt, iv, data } = JSON.parse(atob(walletEntry.encrypted));
  const enc = new TextEncoder();
  const km  = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']);
  const key = await crypto.subtle.deriveKey(
    { name:'PBKDF2', salt:new Uint8Array(salt), iterations:100000, hash:'SHA-256'},
    km, { name:'AES-GCM', length:256 }, false, ['decrypt']
  );
  const dec = await crypto.subtle.decrypt({ name:'AES-GCM', iv:new Uint8Array(iv) }, key, new Uint8Array(data));
  return Keypair.fromSecretKey(Buffer.from(new TextDecoder().decode(dec), 'base64'));
}

async function getLatestTxns(address, limit = 5) {
  try {
    const r = await fetch(`${HELIUS}/addresses/${address}/transactions?api-key=${getHeliusKey()}&limit=${limit}`);
    if (!r.ok) return [];
    return r.json();
  } catch { return []; }
}

async function getTokenAccounts(address) {
  try {
    const conn = new Connection(getHeliusRpcUrl(), 'confirmed');
    const r = await conn.getTokenAccountsByOwner(
      new PublicKey(address),
      { programId: new PublicKey(TOKEN_PGM) },
      { encoding: 'jsonParsed'}
    );
    return (r.value || [])
      .map(a => a.account?.data?.parsed?.info)
      .filter(i => i && parseFloat(i.tokenAmount?.uiAmount || 0) > 0);
  } catch { return []; }
}

async function getTokenPrice(mint) {
  const cached = priceCache.get(mint);
  if (cached && Date.now() - cached.ts < PRICE_CACHE_TTL) return cached.price;

  let result = null;
  try {
    const r = await fetch(`https://price.jup.ag/v4/price?ids=${mint}`);
    const j = await r.json();
    const data = j.data?.[mint];
    if (data && data.price > 0) {
      result = {
        priceUsd: data.price,
        symbol: data.mintSymbol || mint.slice(0,6),
        name: data.mintSymbol || mint.slice(0,10),
        mcap: 0, liq: 0, change24h: 0,
      };
    }
  } catch {}

  if (!result) {
    try {
      const r = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${mint}`);
      const j = await r.json();
      const pair = j?.pairs?.[0];
      if (pair) {
        result = {
          priceUsd: parseFloat(pair.priceUsd || 0),
          symbol: pair.baseToken?.symbol || mint.slice(0,6),
          name: pair.baseToken?.name || mint.slice(0,10),
          mcap: pair.fdv || 0,
          liq: pair.liquidity?.usd || 0,
          change24h: pair.priceChange?.h24 || 0,
        };
      }
    } catch {}
  }

  if (result) {
    priceCache.set(mint, { price: result, ts: Date.now() });
  } else if (cached) {
    // Retourner le dernier prix connu si la requête échoue
    return cached.price;
  }
  return result;
}

async function getTokenBalance(walletAddress, mint) {
  try {
    const conn = new Connection(getHeliusRpcUrl(), 'confirmed');
    const accounts = await conn.getTokenAccountsByOwner(
      new PublicKey(walletAddress),
      { mint: new PublicKey(mint) },
      { encoding: 'jsonParsed'}
    );
    if (!accounts.value.length) return 0;
    return parseFloat(accounts.value[0].account?.data?.parsed?.info?.tokenAmount?.uiAmount || 0);
  } catch { return 0; }
}

function detectBuy(tx, walletAddress) {
  if (tx.type !== 'SWAP') return null;
  const transfers = (tx.tokenTransfers || []).filter(tt =>
    tt.toUserAccount === walletAddress && parseFloat(tt.tokenAmount || 0) > 0
  );
  if (!transfers.length) return null;
  const tt = transfers[0];
  let solSpent = 0;
  for (const nt of tx.nativeTransfers || []) {
    if (nt.fromUserAccount === walletAddress) solSpent += nt.amount / LAMPORTS_PER_SOL;
  }
  return {
    type:'BUY', mint:tt.mint,
    symbol:tt.tokenSymbol||tt.mint.slice(0,6),
    name:tt.tokenName||tt.mint.slice(0,10),
    solSpent, tokenAmount:parseFloat(tt.tokenAmount),
    signature:tx.signature, timestamp:tx.timestamp,
  };
}

function detectSell(tx, walletAddress) {
  if (tx.type !== 'SWAP') return null;
  const transfers = (tx.tokenTransfers || []).filter(tt =>
    tt.fromUserAccount === walletAddress && parseFloat(tt.tokenAmount || 0) > 0
  );
  if (!transfers.length) return null;
  const tt = transfers[0];
  let solReceived = 0;
  for (const nt of tx.nativeTransfers || []) {
    if (nt.toUserAccount === walletAddress) solReceived += nt.amount / LAMPORTS_PER_SOL;
  }
  return {
    type:'SELL', mint:tt.mint,
    symbol:tt.tokenSymbol||tt.mint.slice(0,6),
    name:tt.tokenName||tt.mint.slice(0,10),
    solReceived, tokenAmount:parseFloat(tt.tokenAmount),
    signature:tx.signature, timestamp:tx.timestamp,
  };
}

function calcSellPct(sell, previousBalance) {
  if (!previousBalance || previousBalance <= 0) return 100;
  return Math.min(100, Math.max(1, (sell.tokenAmount / previousBalance) * 100));
}

function hasAlreadyBought(mint, walletId = null) {
  try {
    const trades = JSON.parse(localStorage.getItem('bot-trades') || '[]');
    return trades.some(t =>
      t.mint === mint && t.resultat === 'En cours'&&
      (walletId ? t.botWalletId === walletId : true)
    );
  } catch { return false; }
}

function closeTrade(mint, venteUsd, pnlUsd, roi, walletId = null) {
  try {
    const trades = JSON.parse(localStorage.getItem('bot-trades') || '[]');
    const updated = trades.map(t => {
      const match = t.mint === mint && t.resultat === 'En cours';
      const walletMatch = walletId ? t.botWalletId === walletId : true;
      if (match && walletMatch) {
        return { ...t, venteUsd, pnlUsd, roi, resultat:'Fermé', sortieTs:Math.floor(Date.now()/1000) };
      }
      return t;
    });
    localStorage.setItem('bot-trades', JSON.stringify(updated));
  } catch {}
}export class BotEngine {
  constructor() {
    this.running       = false;
    this.configs       = [];
    this.keypair       = null;
    this.keypairMap    = {};
    this.walletList    = [];
    this.lastTxMap     = {};
    this.wsMap         = {};
    this.interval      = null;
    this.pnlInterval   = null;
    this.priceInterval = null;
    this.solPrice      = 150;
    this.onUpdate      = null;
    this.prevBalances  = {};
    this.simTrailingHighs = {};
    this.stats = { pnlUsd:0, pnlPct:0, openTrades:0, totalInvested:0, positions:[] };
  }

  async executSimBuy(mint, amountSol, walletId = 'sim', onBalanceUpdate = null) {
    try {
      const result = await simBuy(mint, amountSol, this.solPrice, walletId);
      if (onBalanceUpdate) onBalanceUpdate(getSimBalance());
      this._calcPnlSim();
      return result;
    } catch (e) { throw e; }
  }

  async executSimSell(mint, pctToSell, walletId = 'sim', onBalanceUpdate = null) {
    try {
      const pct = parseInt(pctToSell) || 100;
      const result = await simSell(mint, pct, this.solPrice, walletId);
      if (onBalanceUpdate) onBalanceUpdate(getSimBalance());
      this._calcPnlSim();
      return result;
    } catch (e) { throw e; }
  }

  async start(password, configs, onUpdate, walletList = []) {
    if (this.running) return;
    logBotEvent('Démarrage du bot...');
    this.configs    = configs.filter(c => c.enabled && c.targetWallet && c.status !== false);
    this.onUpdate   = onUpdate;
    this.walletList = walletList;
    const neededIds = [...new Set(this.configs.map(c => c.botWalletId).filter(Boolean))];
    if (neededIds.length === 0) {
      try {
        const firstWallet = walletList[0];
        if (firstWallet) {
          const kp = await decryptWallet(firstWallet, password);
          this.keypairMap[firstWallet.id] = kp;
          this.keypair = kp;
          logBotEvent('Wallet bot chargé', { address: kp.publicKey.toBase58() });
        } else {
          const stored = localStorage.getItem('bot-wallet-encrypted');
          if (stored) {
            const { encrypted } = JSON.parse(stored);
            const { salt, iv, data } = JSON.parse(atob(encrypted));
            const enc = new TextEncoder();
            const km  = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']);
            const key = await crypto.subtle.deriveKey({ name:'PBKDF2', salt:new Uint8Array(salt), iterations:100000, hash:'SHA-256'}, km, { name:'AES-GCM', length:256 }, false, ['decrypt']);
            const dec = await crypto.subtle.decrypt({ name:'AES-GCM', iv:new Uint8Array(iv) }, key, new Uint8Array(data));
            this.keypair = Keypair.fromSecretKey(Buffer.from(new TextDecoder().decode(dec), 'base64'));
            logBotEvent('Wallet bot chargé (legacy)', { address: this.keypair.publicKey.toBase58() });
          }
        }
      } catch (e) {
        logBotEvent('Erreur chargement wallet', { error: e.message });
        throw e;
      }
    } else {
      for (const wId of neededIds) {
        const walletEntry = walletList.find(w => w.id === wId);
        if (!walletEntry) { logBotEvent(`Wallet ${wId} introuvable`); continue; }
        if (walletEntry.isSim) {
          logBotEvent('Config sim détectée — pas de déchiffrement pour '+ walletEntry.name);
          continue;
        }
        try {
          const kp = await decryptWallet(walletEntry, password);
          this.keypairMap[wId] = kp;
          logBotEvent('Wallet déverrouillé', { id:wId, address:kp.publicKey.toBase58() });
        } catch (e) {
          logBotEvent('Erreur déverrouillage', { id:wId, error:e.message });
          throw new Error('Mot de passe incorrect pour '+ walletEntry.name);
        }
      }
      this.keypair = Object.values(this.keypairMap)[0] || null;
    }
    const allSim = neededIds.every(id => walletList.find(w => w.id === id)?.isSim);
    if (!this.keypair && !allSim) throw new Error('Aucun wallet disponible');
    this.running  = true;
    this.solPrice = await getSolPrice();
    logBotEvent('' + this.configs.length + 'config(s) active(s)');
    for (const config of this.configs) {
      const txns = await getLatestTxns(config.targetWallet, 1);
      if (txns[0]) this.lastTxMap[config.targetWallet] = txns[0].signature;
    }
    for (const config of this.configs) { this._startWebSocket(config); }
    this.interval      = setInterval(() => this._tickFallback(), 3000);
    this._calcPnl();
    this.pnlInterval   = setInterval(() => this._calcPnl(), 10000);
    this.priceInterval = setInterval(async () => { this.solPrice = await getSolPrice(); }, 60000);
    logBotEvent('Bot actif — WebSocket temps réel + fallback 3s');
    await notifyBotStart(this.configs.length);
  }

  _getKeypairForConfig(config) {
    if (config.botWalletId && this.keypairMap[config.botWalletId]) {
      return this.keypairMap[config.botWalletId];
    }
    return this.keypair;
  }

  _startWebSocket(config) {
    const { targetWallet, name } = config;
    if (this.wsMap[targetWallet]) return;
    try {
      const conn = new Connection(getHeliusRpcUrl(), { wsEndpoint:getHeliusWsUrl(), commitment:'confirmed'});
      const subId = conn.onAccountChange(
        new PublicKey(targetWallet),
        async (accountInfo, context) => {
          if (!this.running) return;
          logBotEvent('Changement détecté sur '+ name + '(WebSocket)', { slot:context.slot });
          try {
            const txns = await getLatestTxns(targetWallet, 3);
            await this._processTxns(txns, config);
          } catch (e) {
            logBotEvent('Erreur WebSocket fetch '+ name, { error:e.message });
          }
        },
        'confirmed');
      this.wsMap[targetWallet] = { conn, subId };
      logBotEvent('WebSocket actif pour '+ name);
    } catch (e) {
      logBotEvent('WebSocket échoué pour '+ name + ', fallback polling', { error:e.message });
    }
  }

  _stopWebSockets() {
    for (const [wallet, ws] of Object.entries(this.wsMap)) {
      try { ws.conn.removeAccountChangeListener(ws.subId); } catch {}
    }
    this.wsMap = {};
  }

  async stop() {
    if (this.interval)      clearInterval(this.interval);
    if (this.pnlInterval)   clearInterval(this.pnlInterval);
    if (this.priceInterval) clearInterval(this.priceInterval);
    this._stopWebSockets();
    this.running    = false;
    this.keypair    = null;
    this.keypairMap = {};
    this.walletList = [];
    logBotEvent('Bot arrêté');
    await notifyBotStop();
  }

  async _tickFallback() {
    if (!this.running) return;
    for (const config of this.configs) {
      if (this.wsMap[config.targetWallet]) continue;
      try {
        const txns = await getLatestTxns(config.targetWallet, 3);
        await this._processTxns(txns, config);
      } catch (e) {
        logBotEvent('Erreur fallback '+ config.name, { error:e.message });
      }
    }
  }

  async _processTxns(txns, config) {
    if (!txns.length) return;
    const { targetWallet, name } = config;
    const lastKnown = this.lastTxMap[targetWallet];
    const newTxns   = lastKnown
      ? txns.filter(tx =>
          tx.signature !== lastKnown &&
          tx.timestamp > (txns.find(t => t.signature === lastKnown)?.timestamp || 0)
        )
      : [txns[0]];
    if (!newTxns.length) return;
    this.lastTxMap[targetWallet] = txns[0].signature;
    const walletEntry = this.walletList.find(w => w.id === config.botWalletId);
    const isSimConfig = walletEntry?.isSim || false;
    const keypair = this._getKeypairForConfig(config);
    for (const tx of newTxns) {
      const buy  = detectBuy(tx, targetWallet);
      const sell = detectSell(tx, targetWallet);
      if (buy) {
        logBotEvent('ACHAT détecté sur '+ name, { token:buy.symbol, sol:buy.solSpent });
        const { allowRebuy, amountMode, amountValue, slippage, jitoTip } = config;
        if (!allowRebuy && hasAlreadyBought(buy.mint, config.botWalletId)) {
          logBotEvent('⏭ Token déjà acheté — ignoré : '+ buy.symbol);
          continue;
        }
        // Mutex : bloque l'achat si une TX est déjà en cours pour ce mint
        const lockKey = buy.mint + (config.botWalletId || '');
        if (buyLocks.has(lockKey)) {
          logBotEvent('⏳ Achat déjà en cours — ignoré : '+ buy.symbol);
          continue;
        }
        buyLocks.add(lockKey);
        let amountSol;
        if (amountMode === 'fixed') {
          amountSol = parseFloat(amountValue) || 0.1;
        } else {
          if (isSimConfig) {
            amountSol = getSimBalance() * (parseFloat(amountValue) / 100);
          } else {
            const conn = new Connection(getHeliusRpcUrl(), 'confirmed');
            const bal  = await conn.getBalance(keypair.publicKey);
            amountSol  = (bal / LAMPORTS_PER_SOL) * (parseFloat(amountValue) / 100);
          }
        }
        if (amountSol < 0.001) { buyLocks.delete(lockKey); logBotEvent('Montant trop faible, ignoré'); continue; }
        if (isSimConfig) {
          try {
            const result = await simBuy(buy.mint, amountSol, this.solPrice, config.botWalletId);
            logBotEvent('Sim copie achat : '+ result.symbol, { sol: amountSol });
            this._calcPnlSim();
            if (this.onUpdate) this.onUpdate(this.stats);
          } catch (e) {
            logBotEvent('Sim achat échoué : '+ e.message);
          } finally {
            buyLocks.delete(lockKey);
          }
          continue;
        }
        // slippage auto = 150bps minimum (0.5% trop faible pour les memecoins)
        const slippageBps = slippage === 'auto' ? 150 : Math.max(parseInt(slippage) * 100, 100);
        try {
          logBotEvent('Copie achat '+ buy.symbol, { sol:amountSol });
          const result = await buyToken({ keypair, tokenMint:buy.mint, amountSol, slippageBps, jitoTip:jitoTip||'low'});
          const achatUsd = amountSol * this.solPrice;
          const tradeData = {
            symbol:buy.symbol, mint:buy.mint,
            entreeTs:Math.floor(Date.now()/1000),
            achatUsd, venteUsd:0, pnlUsd:0, roi:0,
            resultat:'En cours', walletSuivi:targetWallet,
            txHash:result.signature,
            botWalletId: config.botWalletId || null,
            isSim: false,
          };
          logBotTrade(tradeData);
          //  Sauvegarder dans Supabase
          await saveTradeToSupabase(tradeData);
          logBotEvent('Achat copié : '+ buy.symbol, { signature:result.signature });
          await notifyBuy({ symbol:buy.symbol, mint:buy.mint, amountSol, achatUsd, signature:result.signature, walletSuivi:targetWallet });
          setTimeout(() => this._calcPnl(), 5000);
          if (this.onUpdate) this.onUpdate(this.stats);
        } catch (e) {
          logBotEvent('Échec achat '+ buy.symbol, { error:e.message });
          await notifyError({ action:'Achat', symbol:buy.symbol, error:e.message });
        } finally {
          buyLocks.delete(lockKey);
        }
        continue;
      }
      if (sell && config.copySells) {
        logBotEvent('VENTE détectée sur '+ name, { token:sell.symbol, sol:sell.solReceived });
        if (!hasAlreadyBought(sell.mint, config.botWalletId)) { logBotEvent('⏭ On ne possède pas '+ sell.symbol); continue; }
        const prevBal = this.prevBalances[sell.mint] || sell.tokenAmount;
        const pctSold = calcSellPct(sell, prevBal + sell.tokenAmount);
        logBotEvent('Wallet suivi a vendu '+ pctSold.toFixed(0) + '% de '+ sell.symbol);
        if (isSimConfig) {
          try {
            await simSell(sell.mint, pctSold, this.solPrice, config.botWalletId);
            this._calcPnlSim();
          } catch (e) {
            logBotEvent('Sim vente échouée : '+ e.message);
          }
          continue;
        }
        await this._executeSell(sell.mint, sell.symbol, 'COPIE VENTE ('+ name + 'a vendu '+ pctSold.toFixed(0) + '%)', pctSold, config);
        continue;
      }
      if (sell && !config.copySells) logBotEvent('ℹ Vente ignorée (COPY_SELLS=OFF) : '+ sell.symbol);
    }
  }

  async _executeSell(mint, symbol, reason, pctToSell = 100, config = null) {
    const keypair = config ? this._getKeypairForConfig(config) : this.keypair;
    if (!keypair) return;
    try {
      const walletAddr   = keypair.publicKey.toBase58();
      const tokenBalance = await getTokenBalance(walletAddr, mint);
      if (!tokenBalance || tokenBalance <= 0) { logBotEvent('Solde nul pour '+ symbol); return; }
      const amountToSell = tokenBalance * (pctToSell / 100);
      logBotEvent('' + reason + '— Vente '+ pctToSell.toFixed(0) + '% de '+ symbol);
      const result = await sellToken({ keypair, tokenMint:mint, amountTokens:amountToSell, slippageBps:100, jitoTip:'low'});
      const position = this.stats.positions?.find(p => p.mint === mint && p.botWalletId === config?.botWalletId);
      const venteUsd = position?.currentUsd || 0;
      const achatUsd = position?.achatUsd   || 0;
      const pnlUsd   = venteUsd - achatUsd;
      const roi      = achatUsd > 0 ? (pnlUsd / achatUsd) * 100 : 0;
      if (pctToSell >= 100) {
        closeTrade(mint, venteUsd, pnlUsd, roi, config?.botWalletId);
        logBotEvent('Trade fermé : '+ symbol, { reason, pnlUsd:pnlUsd.toFixed(2)+'$', roi:roi.toFixed(1)+'%', signature:result.signature });
        if (reason.includes('STOP-LOSS')) {
          await notifyStopLoss({ symbol, pnlPct:roi, pnlUsd, signature:result.signature });
        } else if (reason.includes('TAKE-PROFIT')) {
          await notifyTakeProfit({ symbol, pnlPct:roi, pnlUsd, signature:result.signature });
        } else {
          await notifySell({ symbol, pctSold:pctToSell, pnlUsd, roi, reason, signature:result.signature });
        }
      } else {
        logBotEvent('Vente partielle '+ symbol + ': '+ pctToSell.toFixed(0) + '%', { signature:result.signature });
        await notifySell({ symbol, pctSold:pctToSell, pnlUsd:null, roi:null, reason, signature:result.signature });
      }
      setTimeout(() => this._calcPnl(), 3000);
      if (this.onUpdate) this.onUpdate(this.stats);
    } catch (e) {
      logBotEvent('Échec vente '+ symbol, { error:e.message });
      await notifyError({ action:'Vente', symbol, error:e.message });
    }
  }async _calcPnlSim() {
    try {
      const trades = JSON.parse(localStorage.getItem('bot-trades') || '[]');
      const simTrades = trades.filter(t => t.resultat === 'En cours'&& t.isSim);
      const simPositions = [];
      for (const trade of simTrades) {
        const priceData = await getTokenPrice(trade.mint);
        if (!priceData) {
          const walletInfo = this.walletList.find(w => w.id === trade.botWalletId);
          simPositions.push({
            mint: trade.mint, symbol: trade.symbol, name: trade.symbol,
            tokenAmount: trade.tokenAmount || 0,
            achatUsd: trade.achatUsd, currentUsd: trade.achatUsd,
            pnlUsd: 0, pnlPct: 0, priceUsd: trade.prixEntree || 0,
            change24h: 0, liq: 0,
            botWalletId: trade.botWalletId,
            walletName: walletInfo?.name || 'Sim Wallet',
            walletIsSim: true,
          });
          continue;
        }
        const tokenAmount = trade.tokenAmount || 0;
        const currentUsd = tokenAmount * priceData.priceUsd;
        const pnlUsd = currentUsd - (trade.achatUsd || 0);
        const pnlPct = trade.achatUsd > 0 ? (pnlUsd / trade.achatUsd) * 100 : 0;
        const walletInfo = this.walletList.find(w => w.id === trade.botWalletId);
        simPositions.push({
          mint: trade.mint, symbol: priceData.symbol, name: priceData.name,
          tokenAmount, achatUsd: trade.achatUsd, currentUsd, pnlUsd, pnlPct,
          priceUsd: priceData.priceUsd, change24h: priceData.change24h, liq: priceData.liq,
          botWalletId: trade.botWalletId,
          walletName: walletInfo?.name || 'Sim Wallet',
          walletIsSim: true,
        });
        const configForWallet = this.configs.find(c => c.botWalletId === trade.botWalletId);
        const stopLossEnabled = configForWallet?.stopLossEnabled ?? true;
        const takeProfitEnabled = configForWallet?.takeProfitEnabled ?? true;
        const stopLoss = configForWallet?.stopLoss ? -Math.abs(parseFloat(configForWallet.stopLoss)) : -50;
        const takeProfit = configForWallet?.takeProfit ? Math.abs(parseFloat(configForWallet.takeProfit)) : 200;
        if (trade.achatUsd > 0) {
          if (stopLossEnabled && pnlPct <= stopLoss) {
            logBotEvent('SIM STOP-LOSS : '+ priceData.symbol + 'à '+ pnlPct.toFixed(1) + '%');
            await simSell(trade.mint, 100, this.solPrice, trade.botWalletId);
          }
          if (takeProfitEnabled && pnlPct >= takeProfit) {
            logBotEvent('🟢 SIM TAKE-PROFIT : '+ priceData.symbol + 'à '+ pnlPct.toFixed(1) + '%');
            await simSell(trade.mint, 100, this.solPrice, trade.botWalletId);
          }
          const trailingEnabled = configForWallet?.trailingStopLossEnabled === true;
          if (trailingEnabled && trade.prixEntree > 0) {
            const trail = Math.abs(parseFloat(configForWallet.trailingStopLoss || 20));
            const curPrice = priceData.priceUsd;
            const tradeKey = trade.txHash || trade.mint;
            if (!this.simTrailingHighs[tradeKey] || curPrice > this.simTrailingHighs[tradeKey]) {
              this.simTrailingHighs[tradeKey] = curPrice;
            }
            const highPrice = this.simTrailingHighs[tradeKey];
            const dropFromHigh = ((curPrice - highPrice) / highPrice) * 100;
            if (highPrice > trade.prixEntree && dropFromHigh <= -trail) {
              logBotEvent('📉 SIM TRAILING-STOP : ' + priceData.symbol + ' high:' + highPrice.toFixed(6) + '$ → now:' + curPrice.toFixed(6) + '$ (' + dropFromHigh.toFixed(1) + '%)');
              await simSell(trade.mint, 100, this.solPrice, trade.botWalletId);
              delete this.simTrailingHighs[tradeKey];
            }
          }
        }
      }
      const livePositions = this.stats.positions.filter(p => !p.walletIsSim);
      const allPositions = [...livePositions, ...simPositions];
      const totalInvested = allPositions.reduce((s, p) => s + (p.achatUsd || 0), 0);
      const totalCurrent  = allPositions.reduce((s, p) => s + (p.currentUsd || 0), 0);
      const pnlUsd = totalCurrent - totalInvested;
      const pnlPct = totalInvested > 0 ? (pnlUsd / totalInvested) * 100 : 0;
      this.stats = { pnlUsd, pnlPct, openTrades: allPositions.length, totalInvested, totalCurrent, positions: allPositions };
      localStorage.setItem('bot-stats', JSON.stringify(this.stats));
      if (this.onUpdate) this.onUpdate(this.stats);
    } catch (e) {
      logBotEvent('Erreur calcul PNL sim', { error: e.message });
    }
  }

  async _calcPnl() {
    if (!this.keypair && Object.keys(this.keypairMap).length === 0) {
      await this._calcPnlSim();
      return;
    }
    try {
      const trades = JSON.parse(localStorage.getItem('bot-trades') || '[]');
      let totalInvested = 0;
      let totalCurrent  = 0;
      const positions   = [];
      const walletsToCheck = Object.keys(this.keypairMap).length > 0
        ? Object.entries(this.keypairMap)
        : this.keypair ? [['default', this.keypair]] : [];
      for (const [walletId, kp] of walletsToCheck) {
        const address  = kp.publicKey.toBase58();
        const accounts = await getTokenAccounts(address);
        const currentMints = new Set(accounts.map(a => a.mint));
        for (const trade of trades.filter(t => t.resultat === 'En cours'&& !t.isSim && (t.botWalletId === walletId || (!t.botWalletId && walletId === 'default')))) {
          if (!currentMints.has(trade.mint)) {
            logBotEvent('Token vendu : '+ trade.symbol + '— fermeture auto');
            closeTrade(trade.mint, 0, 0, 0, walletId);
          }
        }
        for (const acc of accounts) {
          const mint        = acc.mint;
          const tokenAmount = parseFloat(acc.tokenAmount?.uiAmount || 0);
          if (!tokenAmount) continue;
          this.prevBalances[mint] = tokenAmount;
          const matchingTrades = trades.filter(t =>
            t.mint === mint && t.resultat === 'En cours'&& !t.isSim &&
            (t.botWalletId === walletId || (!t.botWalletId && walletId === 'default'))
          );
          const achatUsd = matchingTrades.reduce((s,t) => s + (t.achatUsd||0), 0);
          const priceData = await getTokenPrice(mint);
          if (!priceData) continue;
          const currentUsd = tokenAmount * priceData.priceUsd;
          const pnlUsd     = currentUsd - achatUsd;
          const pnlPct     = achatUsd > 0 ? (pnlUsd / achatUsd) * 100 : 0;
          totalInvested += achatUsd;
          totalCurrent  += currentUsd;
          const walletInfo = this.walletList.find(w => w.id === walletId);
          const position = {
            mint, symbol:priceData.symbol, name:priceData.name,
            tokenAmount, achatUsd, currentUsd, pnlUsd, pnlPct,
            priceUsd:priceData.priceUsd, change24h:priceData.change24h, liq:priceData.liq,
            botWalletId: walletId, walletName: walletInfo?.name || walletId, walletIsSim: false,
          };
          positions.push(position);
          const configForWallet = this.configs.find(c => c.botWalletId === walletId);
          const stopLossEnabled   = configForWallet?.stopLossEnabled   ?? true;
          const takeProfitEnabled = configForWallet?.takeProfitEnabled ?? true;
          const stopLoss   = configForWallet?.stopLoss   ? -Math.abs(parseFloat(configForWallet.stopLoss))   : this._getStopLoss();
          const takeProfit = configForWallet?.takeProfit ?  Math.abs(parseFloat(configForWallet.takeProfit)) : this._getTakeProfit();
          if (achatUsd > 0) {
            if (stopLossEnabled && pnlPct <= stopLoss) {
              logBotEvent('STOP-LOSS : '+ priceData.symbol + 'à '+ pnlPct.toFixed(1) + '%');
              await this._executeSell(mint, priceData.symbol, 'STOP-LOSS ('+ pnlPct.toFixed(1) + '%)', 100, configForWallet || null);
              continue;
            }
            if (takeProfitEnabled && pnlPct >= takeProfit) {
              logBotEvent('🟢 TAKE-PROFIT : '+ priceData.symbol + 'à '+ pnlPct.toFixed(1) + '%');
              await this._executeSell(mint, priceData.symbol, 'TAKE-PROFIT ('+ pnlPct.toFixed(1) + '%)', 100, configForWallet || null);
              continue;
            }
          }
        }
      }
      const simTrades = trades.filter(t => t.resultat === 'En cours'&& t.isSim);
      for (const trade of simTrades) {
        const priceData = await getTokenPrice(trade.mint);
        const tokenAmount = trade.tokenAmount || 0;
        const currentUsd = priceData ? tokenAmount * priceData.priceUsd : trade.achatUsd;
        const pnlUsd = currentUsd - (trade.achatUsd || 0);
        const pnlPct = trade.achatUsd > 0 ? (pnlUsd / trade.achatUsd) * 100 : 0;
        const walletInfo = this.walletList.find(w => w.id === trade.botWalletId);
        positions.push({
          mint: trade.mint, symbol: priceData?.symbol || trade.symbol, name: priceData?.name || trade.symbol,
          tokenAmount, achatUsd: trade.achatUsd, currentUsd, pnlUsd, pnlPct,
          priceUsd: priceData?.priceUsd || 0, change24h: priceData?.change24h || 0, liq: priceData?.liq || 0,
          botWalletId: trade.botWalletId,
          walletName: walletInfo?.name || 'Sim Wallet',
          walletIsSim: true,
        });
        totalInvested += trade.achatUsd || 0;
        totalCurrent  += currentUsd;
      }
      const pnlUsd = totalCurrent - totalInvested;
      const pnlPct = totalInvested > 0 ? (pnlUsd / totalInvested) * 100 : 0;
      this.stats = { pnlUsd, pnlPct, openTrades:positions.length, totalInvested, totalCurrent, positions };
      localStorage.setItem('bot-stats', JSON.stringify(this.stats));
      logBotEvent('PNL mis à jour', { pnlUsd:pnlUsd.toFixed(2), positions:positions.length });
      if (this.onUpdate) this.onUpdate(this.stats);
    } catch (e) {
      logBotEvent('Erreur calcul PNL', { error:e.message });
    }
  }

  _getStopLoss() {
    for (const c of this.configs) { if (c.stopLoss && c.stopLossEnabled !== false) return -Math.abs(parseFloat(c.stopLoss)); }
    return -50;
  }
  _getTakeProfit() {
    for (const c of this.configs) { if (c.takeProfit && c.takeProfitEnabled !== false) return Math.abs(parseFloat(c.takeProfit)); }
    return 200;
  }
}

export const botEngine = new BotEngine();

export function getBotStats() {
  try {
    const saved = localStorage.getItem('bot-stats');
    return saved ? JSON.parse(saved) : { pnlUsd:0, pnlPct:0, openTrades:0, totalInvested:0, positions:[] };
  } catch { return { pnlUsd:0, pnlPct:0, openTrades:0, totalInvested:0, positions:[] }; }
}